import { useEffect, useRef, useState } from 'react';
import { subscribe, snapshot, now, type InFlight } from '../lib/progress';
import { t } from '../lib/i18n';

/**
 * Global progress bar that tracks in-flight fetch requests and, when the Go
 * server has timing history for an endpoint (via /api/eta -> lib/eta), shows a
 * DETERMINISTIC time-proportional bar + an ETA countdown ("≈ 8 s restante").
 * Endpoints with no history yet fall back to the classic indeterminate easing.
 *
 * Mounted once at the top of <App /> (sibling above <Routes>).
 */

export type BarState = { pct: number; etaSec: number | null; overrun: boolean };

/**
 * Pure bar computation (exported for tests). Given the in-flight requests, the
 * current clock, and the current eased fallback pct, returns the bar width, an
 * optional ETA in seconds, and whether the dominant request has overrun its
 * estimate. Deterministic when at least one in-flight request has an expected
 * duration; otherwise it returns the indeterminate eased value.
 */
export function computeBar(recs: InFlight[], nowMs: number, easedPct: number): BarState {
  const known = recs.filter((r) => r.expectedMs != null);
  if (known.length > 0) {
    // Dominant = the request predicted to finish LAST (drives the bar/ETA).
    const dom = known.reduce((a, b) =>
      a.startedAt + (a.expectedMs as number) >= b.startedAt + (b.expectedMs as number) ? a : b,
    );
    const exp = dom.expectedMs as number;
    const elapsed = Math.max(0, nowMs - dom.startedAt);
    if (elapsed < exp) {
      const frac = elapsed / exp; // 0..1
      const pct = 8 + frac * 82; // 8% -> 90%
      const remaining = exp - elapsed;
      // Only show the countdown once past ~0.7s so quick requests don't flash it.
      const etaSec = elapsed > 700 ? Math.max(1, Math.ceil(remaining / 1000)) : null;
      return { pct, etaSec, overrun: false };
    }
    // Overrun — we underestimated. Ease the tail toward 95%; drop the seconds.
    const over = Math.min(1, (elapsed - exp) / Math.max(exp, 1));
    return { pct: 90 + over * 5, etaSec: null, overrun: true };
  }
  // No ETA available → indeterminate easing (original behavior).
  return { pct: easedPct, etaSec: null, overrun: false };
}

export default function GlobalProgress() {
  const [pct, setPct] = useState(0);
  const [etaSec, setEtaSec] = useState<number | null>(null);
  const [overrun, setOverrun] = useState(false);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const easedRef = useRef(8); // eased pct for the indeterminate fallback
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function stopTick() {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    }

    function finish() {
      stopTick();
      setPct(100);
      setEtaSec(null);
      setOverrun(false);
      fadeTimer.current = setTimeout(() => {
        setFading(true);
        fadeTimer.current = setTimeout(() => {
          setVisible(false);
          setFading(false);
          setPct(0);
          easedRef.current = 8;
          fadeTimer.current = null;
        }, 300);
      }, 80);
    }

    function tick() {
      const recs = snapshot();
      if (recs.length === 0) {
        finish();
        return;
      }
      // Advance the indeterminate fallback toward 90% (only matters when no ETA).
      easedRef.current = easedRef.current + (90 - easedRef.current) * 0.08;
      const s = computeBar(recs, now(), easedRef.current);
      setPct(s.pct);
      setEtaSec(s.etaSec);
      setOverrun(s.overrun);
    }

    const unsub = subscribe(() => {
      const recs = snapshot();
      if (recs.length > 0) {
        if (fadeTimer.current) {
          clearTimeout(fadeTimer.current);
          fadeTimer.current = null;
        }
        setFading(false);
        if (!tickRef.current) {
          easedRef.current = 8;
          setVisible(true);
          setPct(8);
          tick();
          tickRef.current = setInterval(tick, 100);
        }
      } else if (tickRef.current) {
        finish();
      }
    });

    return () => {
      unsub();
      stopTick();
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, []);

  if (!visible) return null;

  const showEta = !fading && (etaSec != null || overrun);

  return (
    <>
      <div
        role="progressbar"
        aria-label={t.common.loading}
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '2px',
          zIndex: 60,
          width: `${pct}%`,
          opacity: fading ? 0 : 1,
          transition: 'width 150ms linear, opacity 300ms ease',
        }}
        className="bg-tremor-brand"
      />
      {showEta && (
        <div
          data-testid="global-eta"
          style={{ position: 'fixed', top: 8, right: 10, zIndex: 61 }}
          className="text-xs px-2 py-0.5 rounded-full bg-tremor-brand text-tremor-brand-inverted shadow-sm tabular-nums"
        >
          {overrun ? t.progress.almostDone : `≈ ${etaSec} s ${t.progress.etaSuffix}`}
        </div>
      )}
    </>
  );
}
